import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import QueryRadio from "@builder/components/editable/editor/Query/QueryRadio";
import OliverText from "@builder/components/general/OliverText";
import SelectInput from "@builder/components/general/SelectInput";
import Toggle from "@builder/components/general/Toggle";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty, updateNodeQuery } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { QueryDataTables } from "@common/build/constants";
import { BuildNodeQuery, DynamicGridNode, orderStatus } from "@common/build/types";
import { useEffect, useState, useCallback } from "react";
import useDebouncedCallback from "@builder/hooks/useDebounceCallback";
import "./style.scss";

const filterToQueryMapping = {
    all: {},
    customer: { orderCustomer: "current" },
    outlet: { orderOutlet: "current" },
    station: { orderStation: "current" },
    servedBy: { orderServedBy: "current" },
    selected: { orderCustomer: "selected" }
};

type CurrentFilterKey = keyof typeof filterToQueryMapping;

const OrderQuery = ({ parent }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode) as DynamicGridNode;
    const query = (node?.query || {}) as BuildNodeQuery<"orders">;
    const limit = query.limit || 50;
    const queries = query.queries || {};
    const [orderSource, setOrderSource] = useState(queries.orderSource || "all");
    const [orderStatus, setOrderStatus] = useState(queries.orderStatus || "all");
    const [orderDateRange, setOrderDateRange] = useState(queries.orderDateRange || "all");

    const getInitialFilter = (): CurrentFilterKey => {
        if (queries.orderCustomer === "current") return "customer";
        if (queries.orderCustomer === "selected") return "selected";
        if (queries.orderOutlet === "current") return "outlet";
        if (queries.orderStation === "current") return "station";
        if (queries.orderServedBy === "current") return "servedBy";
        return "all";
    };
    const [currentFilter, setCurrentFilter] = useState<CurrentFilterKey>(getInitialFilter());

    const applyQuery = useCallback(() => {
        const currentFilterQueries = filterToQueryMapping[currentFilter];

        const newQuery: BuildNodeQuery<"orders"> = {
            dataType: QueryDataTables.ORDERS,
            limit,
            queries: {
                orderSource,
                orderStatus,
                orderDateRange,
                orderCustomer: "all",
                orderOutlet: "all",
                orderStation: "all",
                orderServedBy: "all",
                ...currentFilterQueries
            }
        };
        dispatch(updateNodeQuery(newQuery));
    }, [dispatch, limit, orderSource, orderStatus, orderDateRange, currentFilter]);

    const debouncedApplyQuery = useDebouncedCallback(applyQuery, 300);

    // Auto-apply query when any filter changes
    useEffect(() => {
        debouncedApplyQuery();
    }, [orderSource, orderStatus, orderDateRange, currentFilter]);

    return (
        <>
            <SidebarCollapse title="Order list" parent={parent}>
                <Toggle
                    label="Show date separators"
                    checked={!!node.groupBy}
                    callback={value => {
                        dispatch(
                            updateNodeProperty({
                                propertyPath: ["groupBy"],
                                value: "orderDate",
                                remove: !value
                            })
                        );
                    }}
                />
                {node.groupBy && (
                    <SelectInput
                        title="Group by"
                        options={{
                            orderDate: "Order date",
                            updatedAt: "Updated at"
                        }}
                        value={node.groupBy}
                        callback={value => {
                            dispatch(
                                updateNodeProperty({
                                    propertyPath: ["groupBy"],
                                    value
                                })
                            );
                        }}
                    />
                )}
                <div className="dividing-line"></div>

                <OliverText text="Default filter" size="xs" translatePrefix="editable.query." />
                <OliverText
                    className="query-para-text"
                    translatePrefix="editable.query."
                    text="Select a default filter to apply to the list. To allow users to find orders outside of this filtered list, add a filter element."
                    size="xxs"
                />
                <div className="flex-with-ranges">
                    <SelectInput
                        title="Date range"
                        className="full-width-select"
                        options={{
                            all: "All time",
                            thisYear: "This year",
                            thisMonth: "This month",
                            last30Days: "Last 30 days",
                            thisWeek: "This week"
                        }}
                        value={orderDateRange}
                        callback={value => setOrderDateRange(value as "all" | "thisYear" | "thisMonth" | "last30Days" | "thisWeek")}
                    />
                    <SelectInput
                        title="Order source"
                        className="full-width-select"
                        options={{
                            all: "All order sources",
                            "in-store": "POS orders",
                            "woo-commerce": "3rd party orders"
                        }}
                        value={orderSource}
                        callback={value => setOrderSource(value as "all" | "in-store" | "woo-commerce")}
                    />
                    <SelectInput
                        title="Order status"
                        className="full-width-select"
                        options={{
                            all: "All order statuses",
                            completed: "Completed",
                            voidedCancelled: "Voided or cancelled",
                            refunded: "Refunded",
                            partialRefund: "Partial refunded"
                        }}
                        value={orderStatus}
                        callback={value => setOrderStatus(value as orderStatus)}
                    />
                </div>

                <div className="dividing-line"></div>
                <OliverText text="Other filters" size="xs" translatePrefix="editable.query." />

                <QueryRadio
                    selected={currentFilter}
                    options={[
                        { label: "All", value: "all" },
                        { label: "Active customer in cart", value: "customer" },
                        { label: "Selected customer in list", value: "selected" },
                        { label: "Current Outlet", value: "outlet" },
                        { label: "Current Station", value: "station" },
                        { label: "Served by current user", value: "servedBy" }
                    ]}
                    isChecked={(option, selected) => option.value === selected}
                    getLabel={option => option.label}
                    callback={option => setCurrentFilter(option.value as CurrentFilterKey)}
                />
            </SidebarCollapse>
        </>
    );
};

export default OrderQuery;
